/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { Coupon } from '@msdyn365-commerce/retail-proxy';
import { format } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { getCouponMinimumSpendAsync } from '../actions/DataActionExtension.g';
import { ICouponMinimumSpendEntity } from '../actions/DataServiceEntities.g';
import { getUnmetCouponMinimumSpend } from './couponMinimumSpendHelper';

/**
 * Module resources backing the coupon minimum spend messages.
 */
export interface ICouponMinimumSpendMessages {
    /** Used when the site level Coupon Minimum Spend Message is not configured. */
    fallback: string;
    /** Shown when the minimum spend could not be read, so the cart cannot be checked. */
    unavailable: string;
}

/**
 * The outcome of the coupon minimum spend check for a cart.
 */
export interface ICouponMinimumSpendResult {
    /** Why checkout is blocked. Empty when it is not. */
    message: string;
    /** True while the read for the coupons currently on the cart has not completed. */
    isValidating: boolean;
    /** True when checkout and place order must be prevented. */
    isBlocked: boolean;
}

type CouponMinimumSpendStatus = 'idle' | 'loading' | 'loaded' | 'failed';

interface ICouponMinimumSpendState {
    /** The coupon codes the stored result belongs to, so a stale result is not trusted. */
    couponCodesKey: string;
    minimumSpends: ICouponMinimumSpendEntity[];
    status: CouponMinimumSpendStatus;
}

/**
 * Reads the minimum spend for the coupons applied to a cart and reports whether checkout may proceed.
 * Used by the cart and checkout views in both themes. The read runs when the set of applied coupon codes
 * changes, not on every cart change, so adding lines or editing quantities re-compares locally.
 *
 * Checkout is blocked whenever the cart carries a coupon that has not been checked successfully, not only
 * when a minimum is known to be unmet. A failed read, or a read still in flight, leaves it unknown whether
 * the cart qualifies, and there is no server side enforcement behind this to catch a cart that slips through.
 * @param context - The module context, used for the read, the currency format and the site level message.
 * @param coupons - The coupons applied to the cart.
 * @param cartTotal - The cart amount to compare against.
 * @param messages - Module resources used when no site level message is configured, and when the read fails.
 * @returns The message to show, whether the read is still running, and whether checkout is blocked.
 */
export const useCouponMinimumSpend = (
    context: ICoreContext,
    coupons: Coupon[] | undefined,
    cartTotal: number,
    messages: ICouponMinimumSpendMessages
): ICouponMinimumSpendResult => {
    const appliedCouponCodes = (coupons?.map(coupon => coupon.Code) || []).filter((code): code is string => !!code);
    const appliedCouponCodesKey = appliedCouponCodes.join();

    const [state, setState] = React.useState<ICouponMinimumSpendState>({
        couponCodesKey: '',
        minimumSpends: [],
        status: 'idle'
    });

    React.useEffect(() => {
        if (appliedCouponCodes.length === 0) {
            setState({ couponCodesKey: '', minimumSpends: [], status: 'idle' });
            return undefined;
        }

        // Guards against a slow response for a previous set of coupon codes landing after a newer one.
        let isCancelled = false;
        setState({ couponCodesKey: appliedCouponCodesKey, minimumSpends: [], status: 'loading' });

        getCouponMinimumSpendAsync({ callerContext: context.actionContext }, appliedCouponCodes)
            .then(minimumSpends => {
                if (!isCancelled) {
                    setState({ couponCodesKey: appliedCouponCodesKey, minimumSpends, status: 'loaded' });
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setState({ couponCodesKey: appliedCouponCodesKey, minimumSpends: [], status: 'failed' });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [appliedCouponCodesKey]);

    if (appliedCouponCodes.length === 0) {
        return { message: '', isValidating: false, isBlocked: false };
    }

    // Either the read has not started, is still running, or the stored result belongs to a different set of
    // coupons because this render is ahead of the effect. Block, but leave the message off the page.
    if (state.status === 'idle' || state.status === 'loading' || state.couponCodesKey !== appliedCouponCodesKey) {
        return { message: messages.unavailable, isValidating: true, isBlocked: true };
    }

    if (state.status === 'failed') {
        return { message: messages.unavailable, isValidating: false, isBlocked: true };
    }

    const unmetCouponMinimumSpend = getUnmetCouponMinimumSpend(coupons, state.minimumSpends, cartTotal);

    if (!unmetCouponMinimumSpend) {
        return { message: '', isValidating: false, isBlocked: false };
    }

    const appConfig = context.app as {
        [x: string]: any;
    };

    return {
        message: format(
            appConfig.config.couponMinimumSpendMessage || messages.fallback,
            context.cultureFormatter.formatCurrency(unmetCouponMinimumSpend.MinimumSpend)
        ),
        isValidating: false,
        isBlocked: true
    };
};
